<style scoped>
.satellite-controls {
	display: flex;
	gap: 4px;
}

/* ── Vertical (default) ───────────────────────────────── */

.vertical {
	position: absolute;
	top: 10px;
	left: 10px;
	z-index: 1;
	flex-direction: column;
}

.vertical .toolbar-btn {
	width: 36px;
	height: 36px;
}

.vertical .source-trigger {
	width: 36px;
}

.vertical .brightness-panel {
	position: absolute;
	left: 44px;
	bottom: 0;
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	padding: 8px 12px;
	width: 180px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* ── Horizontal ──────────────────────────────────────── */

.horizontal {
	flex-direction: row;
	align-items: center;
}

.horizontal .toolbar-btn {
	width: 32px;
	height: 32px;
}

.horizontal .source-trigger {
	width: auto;
	padding: 0 8px;
	gap: 6px;
}

.horizontal .brightness-panel {
	position: absolute;
	top: 100%;
	right: 0;
	margin-top: 8px;
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	padding: 8px 12px;
	width: 180px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* ── Shared ──────────────────────────────────────────── */

.toolbar-btn {
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	font-size: 1.1rem;
}

.toolbar-btn.active {
	box-shadow: 0 0 0 2px var(--bs-info);
}

/* Group separator between source / base map / overlays */
.divider {
	background: #dee2e6;
	flex-shrink: 0;
}

.horizontal .divider {
	width: 1px;
	height: 22px;
	margin: 0 4px;
}

.vertical .divider {
	width: 100%;
	height: 1px;
	margin: 4px 0;
}

/* ── Imagery source dropdown ─────────────────────────── */

.source-dropdown {
	position: relative;
}

.source-thumb-sm {
	width: 22px;
	height: 22px;
	border-radius: 3px;
	object-fit: cover;
	flex-shrink: 0;
}

.source-trigger-label {
	font-weight: 600;
	font-size: 0.82rem;
	line-height: 1;
	white-space: nowrap;
}

.source-trigger .bi-chevron-down {
	font-size: 0.7rem;
}

.source-trigger:disabled {
	opacity: 0.45;
	cursor: not-allowed;
	filter: grayscale(0.6);
}

.source-menu {
	position: absolute;
	z-index: 20;
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 8px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
	padding: 6px;
	min-width: 240px;
	max-height: 360px;
	overflow-y: auto;
}

.horizontal .source-menu {
	top: calc(100% + 6px);
	left: 0;
}

.vertical .source-menu {
	left: 44px;
	top: 0;
}

.source-option {
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	padding: 6px 8px;
	border: none;
	border-radius: 6px;
	background: transparent;
	text-align: left;
	cursor: pointer;
}

.source-option:hover {
	background: #f1f3f5;
}

.source-option.active {
	background: #e7f5ff;
}

.source-thumb {
	width: 44px;
	height: 44px;
	border-radius: 4px;
	object-fit: cover;
	flex-shrink: 0;
}

.source-meta {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.source-title {
	font-weight: 600;
	font-size: 0.85rem;
	color: #212529;
	line-height: 1.2;
}

.source-desc {
	font-size: 0.72rem;
	color: #6c757d;
	line-height: 1.3;
}

.source-check {
	margin-left: auto;
	color: var(--bs-info);
	font-size: 1rem;
	flex-shrink: 0;
}

.brightness-panel label {
	font-size: 0.8rem;
	color: #6c757d;
	margin-bottom: 2px;
	display: flex;
	justify-content: space-between;
}

.brightness-panel input[type="range"] {
	width: 100%;
}
</style>

<template>
	<div class="satellite-controls" :class="variant" :style="wrapperStyle">
		<!-- Imagery source dropdown -->
		<div class="source-dropdown" ref="dropdownRef">
			<Tooltip
				placement="bottom"
				:title="
					disabled
						? 'Zoom in to see Planet imagery'
						: `Imagery: ${currentSource.title}`
				"
			>
				<button
					class="btn btn-sm toolbar-btn source-trigger"
					:class="sourceOpen ? 'btn-light active' : 'btn-dark'"
					:disabled="disabled"
					@click.stop="toggleSourceMenu"
				>
					<img
						:src="currentSource.thumb"
						class="source-thumb-sm"
						:alt="currentSource.title"
					/>
					<span v-if="variant === 'horizontal'" class="source-trigger-label">{{
						currentSource.short
					}}</span>
					<i class="bi bi-chevron-down"></i>
				</button>
			</Tooltip>
			<div v-if="sourceOpen" class="source-menu">
				<button
					v-for="s in visibleSources"
					:key="s.id"
					class="source-option"
					:class="{ active: s.id === modelValue }"
					@click="selectSource(s.id)"
				>
					<img :src="s.thumb" class="source-thumb" :alt="s.title" />
					<span class="source-meta">
						<span class="source-title">{{ s.title }}</span>
						<span class="source-desc">{{ s.desc }}</span>
					</span>
					<i v-if="s.id === modelValue" class="bi bi-check-lg source-check"></i>
				</button>
			</div>
		</div>

		<div class="divider"></div>

		<Tooltip placement="bottom" title="Brightness">
			<button
				class="btn btn-sm toolbar-btn"
				:class="brightnessEnabled ? 'btn-light active' : 'btn-dark'"
				@click="toggleBrightness"
			>
				<i class="bi bi-brightness-high"></i>
			</button>
		</Tooltip>

		<Tooltip placement="bottom" title="Pool Overlay">
			<button
				class="btn btn-sm toolbar-btn"
				:class="showPools ? 'btn-light active' : 'btn-dark'"
				@click="$emit('update:showPools', !showPools)"
			>
				<i class="bi bi-droplet-fill"></i>
			</button>
		</Tooltip>

		<Tooltip placement="bottom" title="Parcel Map">
			<button
				class="btn btn-sm toolbar-btn"
				:class="showParcels ? 'btn-light active' : 'btn-dark'"
				@click="$emit('update:showParcels', !showParcels)"
			>
				<i class="bi bi-border"></i>
			</button>
		</Tooltip>

		<Tooltip placement="bottom" title="Mosquito Risk (flood irrigation)">
			<button
				class="btn btn-sm toolbar-btn"
				:class="showMosquitoRisk ? 'btn-light active' : 'btn-dark'"
				@click="$emit('update:showMosquitoRisk', !showMosquitoRisk)"
			>
				<i class="bi bi-bug-fill"></i>
			</button>
		</Tooltip>

		<Tooltip
			v-if="showCopy"
			placement="bottom"
			:title="copied ? 'Copied!' : 'Copy tile service URL'"
		>
			<button
				class="btn btn-sm toolbar-btn"
				:class="copied ? 'btn-success' : 'btn-dark'"
				@click="copyRiskURL"
			>
				<i :class="copied ? 'bi bi-check-lg' : 'bi bi-clipboard'"></i>
			</button>
		</Tooltip>

		<div v-if="brightnessEnabled" class="brightness-panel">
			<label>
				<span>Brightness</span>
				<span>{{ (brightness ?? 0).toFixed(1) }}x</span>
			</label>
			<input
				type="range"
				class="form-range"
				:min="0.1"
				:max="20.0"
				:step="0.1"
				:value="brightness ?? 1"
				@input="onBrightnessInput($event)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import Tooltip from "@/components/Tooltip.vue";
import { copyToClipboard, mosquitoRiskTileServiceURL } from "@/lib/tileService";
import {
	IMAGERY_SOURCES,
	STADIA_SOURCE,
	type ImagerySource,
} from "@/map/imagerySources";

interface Props {
	modelValue: string;
	brightness: number | null;
	variant?: "vertical" | "horizontal";
	showPools?: boolean;
	showParcels?: boolean;
	showMosquitoRisk?: boolean;
	stadiaMode?: "road" | "satellite";
	// Show the "copy tile service URL" button. Reveal hides it (the action
	// lives in the Help modal); other pages keep it in the toolbar.
	showCopy?: boolean;
	// Explicit imagery-source menu. Defaults to the full menu (all Planet
	// transforms plus the legacy Stadia source, or just the transforms when
	// stadiaMode is bound). Pages whose imagery is limited — e.g. the live
	// mosaic explorer, which has no locally-tiled bands — pass a subset.
	sources?: ImagerySource[];
	// Disable the imagery source dropdown — Reveal binds this to
	// `mapZoom < PLANET_MIN_ZOOM`, since the planet sources render nothing
	// below that zoom.
	disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	variant: "vertical",
	showPools: true,
	showParcels: true,
	showMosquitoRisk: false,
	showCopy: true,
	disabled: false,
	// NOTE: no default for stadiaMode on purpose — its presence (parent
	// binding v-model:stadiaMode) filters the legacy "stadia" source out of
	// the imagery menu (the basemap toggle now lives in MapLayerControls).
});

const emit = defineEmits<{
	"update:modelValue": [value: string];
	"update:brightness": [value: number | null];
	"update:showPools": [value: boolean];
	"update:showParcels": [value: boolean];
	"update:showMosquitoRisk": [value: boolean];
}>();

const brightnessEnabled = ref(false);
const copied = ref(false);
const sourceOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

function copyRiskURL() {
	copyToClipboard(mosquitoRiskTileServiceURL()).then(() => {
		copied.value = true;
		setTimeout(() => (copied.value = false), 1500);
	});
}

function toggleBrightness() {
	if (brightnessEnabled.value) {
		brightnessEnabled.value = false;
		emit("update:brightness", null);
	} else {
		brightnessEnabled.value = true;
		emit("update:brightness", 0.6);
	}
}

function onBrightnessInput(e: Event) {
	const val = parseFloat((e.target as HTMLInputElement).value);
	emit("update:brightness", val);
}

function selectSource(id: string) {
	emit("update:modelValue", id);
	sourceOpen.value = false;
}

function toggleSourceMenu() {
	if (props.disabled) return;
	sourceOpen.value = !sourceOpen.value;
}

// If the dropdown is open when it becomes disabled (zoom dropped below
// PLANET_MIN_ZOOM mid-gesture), close it.
watch(
	() => props.disabled,
	(disabled) => {
		if (disabled) sourceOpen.value = false;
	},
);

// Close the dropdown on outside click / Escape.
function onDocumentClick(e: MouseEvent) {
	if (
		sourceOpen.value &&
		dropdownRef.value &&
		!dropdownRef.value.contains(e.target as Node)
	) {
		sourceOpen.value = false;
	}
}

function onDocumentKeydown(e: KeyboardEvent) {
	if (e.key === "Escape") sourceOpen.value = false;
}

onMounted(() => {
	document.addEventListener("click", onDocumentClick);
	document.addEventListener("keydown", onDocumentKeydown);
});

onBeforeUnmount(() => {
	document.removeEventListener("click", onDocumentClick);
	document.removeEventListener("keydown", onDocumentKeydown);
});

const wrapperStyle = computed(() => {
	if (props.variant === "horizontal") {
		return { position: "relative" as const };
	}
	return {};
});

// When the parent binds v-model:stadiaMode (Reveal), the legacy "stadia"
// source entry is hidden; other pages keep the plain source selector. A page
// that passes an explicit `sources` list gets exactly that menu.
const visibleSources = computed<ImagerySource[]>(() => {
	if (props.sources) return props.sources;
	return props.stadiaMode === undefined
		? [...IMAGERY_SOURCES, STADIA_SOURCE]
		: IMAGERY_SOURCES;
});

const currentSource = computed(
	() =>
		visibleSources.value.find((s) => s.id === props.modelValue) ??
		visibleSources.value[0],
);
</script>
